import { PatientId, WithPatientId } from '../surgicalEvent.slice';
import { doctorBffApi, InvalidateArr } from '@/modules';
import { MedicalHoService } from './medicalHo.service';
import {
  MedicalHo,
  MedicalHoItem,
  MedicalHoItemCategory
} from './medicalHo.model';
import { wrapError } from '../../error';
import { ID } from '@ianesthesie-kit/services';
import { constructProtocolCacheKey, ProtocolName } from '@/modules/protocol';

export const medicalHoApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    medicalHo: builder.query<MedicalHo, PatientId>({
      queryFn: ({ patientId }) =>
        MedicalHoService.getInstance(patientId).fetch().catch(wrapError),
      providesTags: (result, error, arg) => [
        { type: 'MedicalHo', id: arg.patientId }
      ]
    }),

    addHoItem: builder.mutation<
      MedicalHo,
      WithPatientId<Partial<MedicalHoItem>>
    >({
      queryFn: ({ patientId, ...item }) =>
        MedicalHoService.getInstance(patientId).addItem(item).catch(wrapError),
      invalidatesTags: (result, error, { patientId, qcode }) =>
        addRelevantProtocols({ qcode }, patientId, [
          { type: 'MedicalHo', id: patientId },
          'Dashboard',
          { type: 'SurgeryInfo', id: patientId } // for ASA prediction update
        ])
    }),

    deleteHoItem: builder.mutation<MedicalHo, WithPatientId<MedicalHoItem>>({
      queryFn: ({ patientId, ...item }) =>
        MedicalHoService.getInstance(patientId)
          .deleteItem(item)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId, qcode }) =>
        addRelevantProtocols({ qcode }, patientId, [
          { type: 'MedicalHo', id: patientId },
          'Dashboard',
          { type: 'SurgeryInfo', id: patientId } // for ASA prediction update
        ])
    }),

    resetHoCategory: builder.mutation<
      MedicalHo,
      WithPatientId<{ category: MedicalHoItemCategory }>
    >({
      queryFn: ({ patientId, category }) =>
        MedicalHoService.getInstance(patientId)
          .resetCategory(category)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId, category }) =>
        addProtocolTags(PROTOCOL_BY_CATEGORY[category], patientId, [
          { type: 'MedicalHo', id: patientId },
          'Dashboard',
          { type: 'SurgeryInfo', id: patientId }
        ])
    })
  }),
  overrideExisting: false
});

const PROTOCOL_BY_CATEGORY: Partial<
  Record<MedicalHoItemCategory, ProtocolName>
> = {
  [MedicalHoItemCategory.CARDIO_VASCULAR]: ProtocolName.CARDIAC_STRESS_TEST,
  [MedicalHoItemCategory.PULMONARY]: ProtocolName.NICOTINE
};

const SMOKER_QCODES = [
  'Smoker',
  'SmokerWantToStop',
  'SmokerCigPerDay',
  'SmokerFirstCigDelay',
  'SmokerIntensity'
];

/** Tags of the protocols whose status depends on this MHO item, if any. */
function addRelevantProtocols(
  mhoItem: Partial<MedicalHoItem>,
  patientId: ID,
  arr: InvalidateArr
) {
  return addProtocolTags(protocolForQcode(mhoItem?.qcode), patientId, arr);
}

function protocolForQcode(qcode?: string): ProtocolName | undefined {
  if (qcode === 'CoronaryBypass') {
    return ProtocolName.CARDIAC_STRESS_TEST;
  }
  if (qcode && SMOKER_QCODES.includes(qcode)) {
    return ProtocolName.NICOTINE;
  }
  return undefined;
}

function addProtocolTags(
  protocol: ProtocolName | undefined,
  patientId: ID,
  arr: InvalidateArr
) {
  if (protocol) {
    arr.push(
      {
        type: 'Protocol',
        id: constructProtocolCacheKey({ patientId, protocol })
      },
      { type: 'ProtocolStatuses', id: patientId }
    );
  }
  return arr;
}

export const {
  useMedicalHoQuery,
  useDeleteHoItemMutation,
  useAddHoItemMutation,
  useResetHoCategoryMutation
} = medicalHoApi;
